import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NotificationBadge, NotificationPanel } from "./NotificationBell";
import { useNotifications } from "@/hooks/useNotifications";
import { useNewProjectsBadge } from "@/hooks/useNewProjectsBadge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { 
  Menu, 
  X, 
  User, 
  LogOut, 
  Settings, 
  Folder, 
  LayoutGrid,
  ChevronDown,
  ChevronRight,
  Compass,
  ShieldCheck,
  BookOpen
} from "lucide-react";
import { cn } from "@/lib/utils";
import { componentCategories } from "@/data/componentCategories";
import { supabase } from "@/integrations/supabase/client";
import { AuthModal } from "@/components/auth/AuthModal";
import minzoLogo from "@/assets/minzo-logo.webp";

const baseNavigation = [
  { name: "About", href: "/#about", icon: Compass, isAnchor: true },
  { name: "Concierge", href: "/#concierge", icon: LayoutGrid, isAnchor: true },
  { name: "Solutions", href: "/cases", icon: BookOpen },
];

const authOnlyNavigation: typeof baseNavigation = [
  { name: "My Projects", href: "/projects", icon: Folder },
];

interface HeaderProps {
  user?: { email: string; name?: string } | null;
  onSignOut?: () => void;
  transparent?: boolean;
}

// Mega Menu Component
function ComponentsMegaMenu({ isTransparent }: { isTransparent: boolean }) {
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
  const activeCategory = componentCategories.find((c) => c.id === hoveredCategory);

  return (
    <div className="pointer-events-none absolute left-0 top-full z-50 pt-2 opacity-0 transition-all duration-300 group-hover/mega:pointer-events-auto group-hover/mega:opacity-100">
      <div className="animate-fade-in flex rounded-xl border border-border bg-background/95 shadow-2xl backdrop-blur-md">
        {/* Main Categories Column */}
        <div className="w-64 border-r border-border p-4">
          <div className="mb-3 px-2">
            <h3 className="font-serif text-sm font-semibold text-foreground">Categories</h3>
          </div>
          <div className="space-y-1">
            {componentCategories.map((mainCat) => {
              const IconComponent = mainCat.icon;
              return (
                <Link
                  key={mainCat.id}
                  to={`/components?category=${mainCat.id}`}
                  onMouseEnter={() => setHoveredCategory(mainCat.id)}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-all duration-200",
                    hoveredCategory === mainCat.id
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-secondary"
                  )}
                >
                  <IconComponent className="h-4 w-4 shrink-0" />
                  <span className="flex-1 font-medium">{mainCat.name}</span>
                  <ChevronRight className={cn(
                    "h-4 w-4 shrink-0 transition-transform duration-200",
                    hoveredCategory === mainCat.id && "translate-x-0.5"
                  )} />
                </Link>
              );
            })}
          </div>
          <div className="mt-4 border-t border-border pt-4">
            <Link 
              to="/components" 
              className="flex items-center gap-2 px-3 text-sm text-primary hover:underline"
            >
              View all components →
            </Link>
          </div>
        </div>

        {/* Subcategories Panel */}
        <div className="w-72 p-4">
          {activeCategory ? (
            <div className="animate-fade-in">
              <div className="mb-3 px-2">
                <h4 className="font-serif text-sm font-semibold text-foreground">
                  {activeCategory.name}
                </h4>
              </div>
              <div className="space-y-1">
                {activeCategory.subCategories.map((subCat) => (
                  <Link
                    key={subCat.id}
                    to={`/components?category=${activeCategory.id}&sub=${subCat.id}`}
                    className="block rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                  >
                    {subCat.name}
                  </Link>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              <p>Hover a category to see subcategories</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function Header({ user, onSignOut, transparent = false }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { unreadCount } = useNotifications();
  const { hasNew: hasNewProjects, markSeen: markProjectsSeen } = useNewProjectsBadge();

  const navigation = [...baseNavigation, ...(user ? authOnlyNavigation : [])];

  // Check if user is admin
  useEffect(() => {
    const checkAdminRole = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setIsAdmin(false);
        return;
      }

      const { data } = await supabase.rpc('has_role', {
        _user_id: session.user.id,
        _role: 'admin'
      });

      setIsAdmin(data === true);
    };

    if (user) {
      checkAdminRole();
    } else {
      setIsAdmin(false);
    }
  }, [user]);

  const isActive = (href: string) => location.pathname === href;

  // Mark projects as seen when on /projects page
  useEffect(() => {
    if (location.pathname === "/projects" && user) {
      markProjectsSeen();
    }
  }, [location.pathname, user, markProjectsSeen]);

  useEffect(() => {
    if (!transparent) return;

    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [transparent]);

  const isTransparent = transparent && !scrolled;

  return (
    <>
    <header
      className={cn(
        "fixed top-0 z-50 w-full transition-all duration-300",
        isTransparent
          ? "border-transparent bg-transparent"
          : "border-b border-border/40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60"
      )}
    >
      <nav className="container mx-auto flex h-16 items-center justify-between px-4 lg:px-8">
        {/* Logo */}
        <Link to="/" className="group/logo flex h-16 items-center translate-y-[0.175rem]">
          <img 
            src={minzoLogo} 
            alt="MINZO" 
            className={cn(
              "h-[4.5rem] w-auto transition-all duration-300 group-hover/logo:scale-105",
              isTransparent && "brightness-0 invert"
            )}
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden items-center gap-1 md:flex">
          {navigation.map((item) => (
            item.isAnchor ? (
              <a
                key={item.name}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  const id = item.href.split('#')[1];
                  if (location.pathname !== '/') {
                    navigate('/');
                    setTimeout(() => {
                      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  } else {
                    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className={cn(
                  "group/nav relative flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300",
                  isTransparent
                    ? "text-white/70 hover:bg-white/10 hover:text-white"
                    : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
                )}
              >
                <item.icon className="h-4 w-4 transition-all duration-300 group-hover/nav:scale-110 group-hover/nav:rotate-3" />
                <span className="relative">
                  {item.name}
                  <span className="absolute -bottom-0.5 left-0 h-[2px] w-0 bg-current transition-all duration-300 group-hover/nav:w-full" />
                </span>
              </a>
            ) : (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  "group/nav relative flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300",
                  isActive(item.href)
                    ? isTransparent
                      ? "bg-white/20 text-white"
                      : "bg-secondary text-foreground"
                    : isTransparent
                      ? "text-white/70 hover:bg-white/10 hover:text-white"
                      : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
                )}
              >
                <item.icon className={cn(
                  "h-4 w-4 transition-all duration-300",
                  !isActive(item.href) && "group-hover/nav:scale-110 group-hover/nav:rotate-3"
                )} />
                <span className="relative">
                  {item.name}
                  {!isActive(item.href) && (
                    <span className="absolute -bottom-0.5 left-0 h-[2px] w-0 bg-current transition-all duration-300 group-hover/nav:w-full" />
                  )}
                  {item.name === "My Projects" && hasNewProjects && (
                    <span className="absolute -right-3 -top-1 flex h-2 w-2 items-center justify-center rounded-full bg-destructive animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite]" />
                  )}
                </span>
              </Link>
            )
          ))}
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-3">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className={cn(
                    "relative gap-2 px-3",
                    isTransparent && "text-white hover:bg-white/10"
                  )}
                >
                  <div
                    className={cn(
                      "relative flex h-8 w-8 items-center justify-center rounded-full",
                      isTransparent ? "bg-white/20" : "bg-primary/10"
                    )}
                  >
                    <User className={cn("h-4 w-4", isTransparent ? "text-white" : "text-primary")} />
                    <NotificationBadge isTransparent={isTransparent} />
                  </div>
                  <span className={cn("hidden text-sm font-medium sm:block", isTransparent && "text-white")}>
                    {user.name || user.email.split("@")[0]}
                  </span>
                  <ChevronDown className={cn("h-4 w-4", isTransparent ? "text-white/70" : "text-muted-foreground")} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72 bg-popover p-0">
                <NotificationPanel />
                <DropdownMenuSeparator className="my-0" />
                {isAdmin && (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/admin")} className="px-4 py-2.5">
                      <ShieldCheck className="mr-2 h-4 w-4" />
                      Admin Dashboard
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="my-0" />
                  </>
                )}
                <DropdownMenuItem onClick={() => navigate("/profile")} className="px-4 py-2.5">
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/settings")} className="px-4 py-2.5">
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator className="my-0" />
                <DropdownMenuItem onClick={onSignOut} className="px-4 py-2.5 text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="ghost"
              onClick={() => setShowAuthModal(true)}
              className={cn(
                "hidden sm:flex",
                isTransparent && "text-white hover:bg-white/10"
              )}
            >
              Sign In
            </Button>
          )}

          {/* Mobile Menu */}
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild className="md:hidden">
              <Button
                variant="ghost"
                size="icon"
                className={cn(isTransparent && "text-white hover:bg-white/10")}
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 bg-background">
              <div className="flex flex-col gap-6 pt-6">
                <div className="flex items-center justify-between">
                  <span className="font-serif text-lg font-semibold">Menu</span>
                  <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)}>
                    <X className="h-5 w-5" />
                  </Button>
                </div>
                <div className="flex flex-col gap-2">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setMobileMenuOpen(false)}
                      className={cn(
                        "flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors",
                        isActive(item.href)
                          ? "bg-secondary text-foreground"
                          : "text-muted-foreground hover:bg-secondary/50 hover:text-foreground"
                      )}
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="relative">
                        {item.name}
                        {item.name === "My Projects" && hasNewProjects && (
                          <span className="absolute -right-3 top-0 flex h-2 w-2 rounded-full bg-destructive animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite]" />
                        )}
                      </span>
                    </Link>
                  ))}
                </div>
                {!user && (
                  <div className="flex flex-col gap-2 border-t pt-4">
                    <Button variant="outline" onClick={() => { setShowAuthModal(true); setMobileMenuOpen(false); }}>
                      Sign In
                    </Button>
                  </div>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
    <AuthModal open={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </>
  );
}
